/**
 *
 * @author Matthew Rawson @ 14/02/2018 17:14
 * @description
 * @notes
 *
 * I've maintained the split of functionality between schedule list and schedule edit to future proof when we
 * move to showing the schedule list as an actual list, then have the schedule edit pages driven by the url instead
 * of the schedule being passed into this component.
 *
 */
import React, {Component, Children} from "react"
import PropTypes from "prop-types"
import {Row} from "react-bootstrap"
import {get, set} from "lodash"

import id from "@irati/utils/id"
import {injectRoute} from "@irati/utils/helpers/routing"
import Loading from "@irati/ui/src/Loading"
import {Card} from "@irati/ui/src/Layout"
import notify from "@irati/utils/notify"
import {LoadingServerError} from "@irati/ui/src/Errors"
import Http404 from "@irati/ui/src/Errors/Http404"
import {updateSchedule} from "@irati/insights/services/schedule"
import authentication from "@irati/auth"
import padding from "@irati/ui/src/global/padding"

import {getSchedule} from "../../graph/schedule"
import {MenuItem, Menu} from "../Menu"
import {ScheduleCard, ScheduleStatusButton, ActionsContainer} from "./"
import {Breadcrumbs} from "../../components/Navigation"
import {Page} from "../../components/Layout"
import sitemap from "../../routes/sitemap"
import tracking from "../../tracking/survey"

/**
 * Internal const
 *
 * @type {string}
 * @private
 */
const _SUCCESS_MESSAGE = "Schedule updated"


class ScheduleEdit extends Component {
  static propTypes = {
    children: PropTypes.node,
    match: PropTypes.shape({
      params: PropTypes.object
    })
  }
  static defaultProps = {
    children: null,
    match: {
      params: {}
    }
  }


  constructor(props) {
    super(props)
    this.state = {
      load: this.setScheduleToState(props.match.params.scheduleId),
      schedule: null
    }
  }


  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.match.params.scheduleId !== nextProps.match.params.scheduleId) {
      this.setScheduleToState(nextProps.match.params.scheduleId)
    }
  }


  setScheduleToState = (scheduleId) => {
    const tenant = authentication.userTenant().uuid
    const load = getSchedule({tenant, id: scheduleId}).then((schedule) => {
      this.setState({schedule})
    })
    return load
  }


  /**
   * Handle changes from fields and assign to the state object
   *
   * @param schedule
   * @param key
   * @param value
   */
  handleChange = (schedule, key, value) => {
    set(schedule, key, value)
    this.setState({schedule})
  }


  /**
   * Handle form submit to update the schedule object and fetch the latest version
   *
   * @param schedule
   * @returns {*}
   */
  handleSubmit = () => {
    const submit = updateSchedule(this.state.schedule).then(() => {
      return this.setScheduleToState(this.state.schedule.id).then(() => {
        tracking.saveSurveySettings()
        notify.success({message: _SUCCESS_MESSAGE, position: "bottom-right"})
        return injectRoute(sitemap.scheduleEdit.url, {scheduleId: this.state.schedule.id})
      })
    })

    this.setState({submit})

    /**
     * We want to return a caught error to the async router link otherwise it throws the error and breaks the page.
     * But we want to set the uncaught promise to state for use in `Loading` to display the Error properly
     */
    return submit.catch(() => {
      tracking.errorsEmployeeEngagement()
    })
  }
  render() {
    const idNamespace = id("ScheduleEdit", this.props)
    const {schedule, load} = this.state
    const {children, match} = this.props
    const reloadSchedule = () => this.setScheduleToState(schedule.id)
    const scheduleRouteContext = {scheduleId: get(schedule, "id")}

    /** ComponentProps is used to forward props through <NestedRoute>'s */
    const clonedChildren = Children.map(children, (child, index) => {
      return React.cloneElement(child, {
        componentProps: {
          id: id(get(child, "props.path") || index, idNamespace),
          onChange: this.handleChange,
          onSubmit: this.handleSubmit,
          schedule,
          reload: reloadSchedule
        }
      })
    })

    const crumbs = [
      sitemap.homepage,
      {...sitemap.scheduleEdit, values: {schedule: get(schedule, "reference")}}
    ]

    return (
      <Page id={idNamespace}>
        <Breadcrumbs id={idNamespace} crumbs={crumbs} match={match} load={load} />
        <Card>
          <Loading load={this.state.load} Error={Http404}>
            <Row>
              <ScheduleCard schedule={schedule} includeSurveyCycle={false}>
                <ActionsContainer>
                  <ScheduleStatusButton
                    onChange={reloadSchedule}
                    schedule={schedule}
                    onErrorRedirectTo={injectRoute(sitemap.scheduleSettings.url, scheduleRouteContext)} />
                </ActionsContainer>
              </ScheduleCard>
            </Row>

            {schedule && (
              <Menu id={id("menu", idNamespace)} margin={padding.base}>
                <MenuItem
                  id={id("link-survey-settings", idNamespace)}
                  to={injectRoute(sitemap.scheduleSettings.url, scheduleRouteContext)}>
                  Survey Settings
                </MenuItem>
                <MenuItem
                  id={id("link-email-settings", idNamespace)}
                  to={injectRoute(sitemap.emailSettings.url, scheduleRouteContext)}>
                  Email Settings
                </MenuItem>
                <MenuItem
                  id={id("link-question-library", idNamespace)}
                  to={injectRoute(sitemap.questionLibrary.url, scheduleRouteContext)}>
                  Question Library
                </MenuItem>
              </Menu>
            )}

            <Loading load={this.state.submit} Error={LoadingServerError} />

            {clonedChildren}

          </Loading>
        </Card>
      </Page>
    )
  }
}

export default ScheduleEdit
